import { type ChangeEvent, useEffect, useRef } from 'react'
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/shadcn/select'
import { Avatar } from '@/components/ui/shadcn/avatar'
import { Textarea } from '../ui/shadcn/textarea'
import { type Accounts } from '@backend/lib/db/schema/platform'
import { Button } from './shadcn/button'
import { type PostContent as TikTokPostContent } from '@backend/lib/platforms/tiktok'

type Props = {
  accounts: Accounts | undefined
  selectedAccountId: string | undefined
  postContent: TikTokPostContent
  onChangeCaption?: (e: ChangeEvent<HTMLTextAreaElement>) => void
  onChangeAccountId: (accountId: string) => void
  onStartAuth: () => void
}

export function TikTokPostPreview({
  accounts,
  selectedAccountId,
  postContent,
  onChangeCaption,
  onChangeAccountId,
  onStartAuth,
}: Props) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }, [postContent.caption])

  if (!accounts || accounts.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-200">
            <svg
              className="h-6 w-6 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900">Connect TikTok</h3>
          <p className="mt-2 text-sm text-gray-500">Connect your TikTok account to start posting</p>
          <Button onClick={onStartAuth} className="mt-4">
            Connect TikTok
          </Button>
        </div>
      </div>
    )
  }

  const selectedAccount =
    accounts.find((account) => account.id === selectedAccountId) || accounts[0]

  return (
    <div className="flex h-full w-full flex-col rounded-lg border border-gray-200 bg-white">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-gray-200 p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-black">
            <svg className="h-5 w-5 text-white" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
            </svg>
          </div>
          <div>
            {accounts.length === 1 ? (
              // Show single account without selector
              <div className="flex items-center gap-2">
                <Avatar className="h-6 w-6">
                  <img src={selectedAccount?.imageUrl} alt={selectedAccount?.displayName} />
                </Avatar>
                <span className="text-sm font-medium">{selectedAccount?.displayName}</span>
              </div>
            ) : (
              // Show selector for multiple accounts
              <Select value={selectedAccountId} onValueChange={onChangeAccountId}>
                <SelectTrigger className="w-[200px]">
                  <SelectContent>
                    {accounts.map((account) => (
                      <SelectItem key={account.id} value={account.id}>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <img src={account.imageUrl} alt={account.displayName} />
                          </Avatar>
                          <span>{account.displayName}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </SelectTrigger>
              </Select>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-4">
        <div className="space-y-4">
          {/* Video/Image Preview */}
          {postContent.type === 'video' && postContent.url && (
            <div className="aspect-9/16 mx-auto w-full max-w-[300px] overflow-hidden rounded-lg bg-gray-100">
              <video src={postContent.url} className="h-full w-full object-cover" controls muted />
            </div>
          )}

          {/* Caption */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Caption</label>
            <Textarea
              ref={textareaRef}
              value={postContent.caption || ''}
              onChange={onChangeCaption}
              placeholder="What's on your mind?"
              className="min-h-[100px] resize-none whitespace-pre-wrap border-gray-300 focus:border-black focus:ring-black"
              maxLength={2200}
            />
            <div className="text-right text-xs text-gray-500">
              {(postContent.caption || '').length}/2200
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
